import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { db } from '../lib/supabase';

// ── MFASetup: enroll TOTP — shown in ProfilePage ─────────────────────────────
export function MFASetup({ onDone }) {
  const [step,     setStep]     = useState('idle'); // idle | qr | verify | done | already
  const [qr,       setQr]       = useState('');
  const [secret,   setSecret]   = useState('');
  const [factorId, setFactorId] = useState('');
  const [code,     setCode]     = useState('');
  const [err,      setErr]      = useState('');
  const [loading,  setLoading]  = useState(false);

  // Check if already enrolled on mount
  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data }) => {
      if (data?.totp?.length > 0) setStep('already');
    });
  }, []);

  const enroll = async () => {
    setLoading(true); setErr('');
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', issuer: 'Aspreo' });
    if (error) { setErr(error.message); setLoading(false); return; }
    setQr(data.totp.qr_code);
    setSecret(data.totp.secret);
    setFactorId(data.id);
    setStep('qr');
    setLoading(false);
  };

  const verify = async () => {
    setLoading(true); setErr('');
    const { data: ch, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
    if (cErr) { setErr(cErr.message); setLoading(false); return; }
    const { error: vErr } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code });
    if (vErr) { setErr('Invalid code — try again.'); setLoading(false); return; }
    setStep('done');
    setLoading(false);
    if (onDone) onDone();
  };

  const unenroll = async () => {
    setLoading(true);
    const { data } = await supabase.auth.mfa.listFactors();
    const totp = data?.totp?.[0];
    if (totp) await supabase.auth.mfa.unenroll({ factorId: totp.id });
    setStep('idle');
    setLoading(false);
  };

  return (
    <div style={card}>
      <h3 style={hd}>🔐 Two-Factor Authentication</h3>

      {step === 'already' && (
        <>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
            <span style={{ fontSize:18 }}>✅</span>
            <span style={{ color:'#34d399', fontWeight:600, fontSize:14 }}>2FA is active on your account</span>
          </div>
          <p style={sub}>You will be asked for your authenticator code each time you log in and access the Vault.</p>
          <button onClick={unenroll} disabled={loading} style={{ ...pb, background:'#7f1d1d', marginTop:8 }}>
            {loading ? 'Removing...' : '🗑 Disable 2FA'}
          </button>
        </>
      )}

      {step === 'idle' && (
        <>
          <p style={sub}>Add an extra layer of security. You'll need an authenticator app (Google Authenticator, Authy, 1Password, etc.)</p>
          <button onClick={enroll} disabled={loading} style={pb}>
            {loading ? 'Setting up...' : '📱 Enable 2FA'}
          </button>
        </>
      )}

      {step === 'qr' && (
        <>
          <p style={sub}>1. Scan this QR code with your authenticator app:</p>
          <div style={{ textAlign:'center', margin:'12px 0' }}>
            <img src={qr} alt="QR Code" style={{ width:180, height:180, borderRadius:8, border:'2px solid rgba(99,102,241,.3)', background:'#fff', padding:4 }}/>
          </div>
          <p style={{ ...sub, wordBreak:'break-all' }}>
            Or enter key manually: <code style={{ color:'#818cf8', fontSize:11, background:'rgba(99,102,241,.1)', padding:'2px 6px', borderRadius:4 }}>{secret}</code>
          </p>
          <p style={sub}>2. Enter the 6-digit code shown in your app to confirm:</p>
          <input
            value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
            placeholder="000000" maxLength={6} autoFocus
            onKeyDown={e=>e.key==='Enter'&&code.length===6&&verify()}
            style={{ ...inp, textAlign:'center', fontSize:22, letterSpacing:'0.35em', marginBottom:10 }}
          />
          {err && <div style={errSt}>{err}</div>}
          <button onClick={verify} disabled={loading || code.length < 6} style={pb}>
            {loading ? 'Verifying...' : '✓ Verify & Activate'}
          </button>
        </>
      )}

      {step === 'done' && (
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:40, marginBottom:8 }}>✅</div>
          <p style={{ color:'#34d399', margin:0, fontWeight:600, fontSize:14 }}>
            Two-factor authentication is now active!
          </p>
          <p style={{ ...sub, marginTop:8 }}>Next time you log in, you'll be asked for your authenticator code.</p>
        </div>
      )}
    </div>
  );
}

// ── MFALoginChallenge: shown after login when AAL2 is required ───────────────
export function MFALoginChallenge({ onVerified }) {
  const [code,          setCode]          = useState('');
  const [err,           setErr]           = useState('');
  const [loading,       setLoading]       = useState(false);
  const [factorId,      setFactorId]      = useState('');
  const [rememberMe,    setRememberMe]    = useState(false);
  const [checking,      setChecking]      = useState(true);

  // Stable device token for login (separate key from vault's token)
  const getLoginDeviceToken = () => {
    let t = localStorage.getItem('login_device_token');
    if (!t) { t = `ld_${Date.now()}_${Math.random().toString(36).slice(2,10)}`; localStorage.setItem('login_device_token', t); }
    return t;
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.auth.mfa.listFactors();
        const totp = data?.totp?.[0];
        if (!totp) { onVerified(); return; }
        setFactorId(totp.id);

        // Check if this device is remembered for login
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const token = getLoginDeviceToken();
          const { data: sess } = await supabase
            .from('vault_sessions')
            .select('*')
            .eq('user_id', user.id)
            .eq('device_token', token)
            .eq('session_type', 'login')
            .gt('expires_at', new Date().toISOString())
            .maybeSingle();
          if (sess) { onVerified(); return; }
        }
      } catch { onVerified(); return; }
      setChecking(false);
    })();
  }, [onVerified]);

  const verify = async () => {
    if (!factorId) return;
    setLoading(true); setErr('');
    try {
      const { data: ch, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
      if (cErr) throw cErr;
      const { error: vErr } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code });
      if (vErr) throw new Error('Invalid code — try again.');

      // Save login device session if remember me was checked
      if (rememberMe) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const token = getLoginDeviceToken();
          const expires = new Date();
          expires.setDate(expires.getDate() + 30);
          await supabase.from('vault_sessions').upsert(
            { user_id: user.id, device_token: token, expires_at: expires.toISOString(), session_type: 'login' },
            { onConflict: 'user_id,device_token,session_type' }
          );
        }
      }
      onVerified();
    } catch(e) { setErr(e.message); }
    setLoading(false);
  };

  if (checking) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg,#0f172a,#1e1b4b)', fontFamily:"'Inter',sans-serif" }}>
      <div style={{ color:'#94a3b8', fontSize:14 }}>Checking security...</div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg,#0f172a,#1e1b4b)', padding:20, fontFamily:"'Inter',sans-serif" }}>
      <div style={{ background:'#1e293b', border:'1px solid rgba(255,255,255,.1)', borderRadius:20, padding:36, width:'100%', maxWidth:380, boxShadow:'0 24px 80px rgba(0,0,0,.5)', textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:12 }}>🔐</div>
        <h2 style={{ margin:'0 0 8px', color:'#f1f5f9', fontSize:20, fontWeight:700 }}>Two-Factor Authentication</h2>
        <p style={{ margin:'0 0 24px', color:'#94a3b8', fontSize:14, lineHeight:1.6 }}>
          Enter the 6-digit code from your authenticator app to continue.
        </p>
        <input
          value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
          placeholder="000000" maxLength={6} autoFocus
          onKeyDown={e=>e.key==='Enter'&&code.length===6&&verify()}
          style={{ width:'100%', padding:'12px', background:'#0f172a', border:'1px solid rgba(255,255,255,.15)', borderRadius:10, color:'#f1f5f9', WebkitTextFillColor:'#f1f5f9', fontFamily:"'Inter',sans-serif", fontSize:24, textAlign:'center', letterSpacing:'0.4em', outline:'none', boxSizing:'border-box', marginBottom:12, WebkitBoxShadow:'0 0 0 1000px #0f172a inset', caretColor:'#f1f5f9' }}
        />
        {err && <div style={{ color:'#f87171', fontSize:13, background:'rgba(244,63,94,.08)', border:'1px solid rgba(244,63,94,.2)', borderRadius:6, padding:'8px 12px', marginBottom:12 }}>{err}</div>}
        <label style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, fontSize:13, color:'#94a3b8', cursor:'pointer', marginBottom:18, userSelect:'none' }}>
          <input type="checkbox" checked={rememberMe} onChange={e=>setRememberMe(e.target.checked)} style={{ accentColor:'#6366f1', width:15, height:15 }}/>
          Remember this device for 30 days
        </label>
        <button onClick={verify} disabled={loading || code.length < 6}
          style={{ width:'100%', padding:'12px', borderRadius:10, border:'none', background:(loading||code.length<6)?'#334155':'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'#fff', fontFamily:"'Inter',sans-serif", fontWeight:700, fontSize:15, cursor:(loading||code.length<6)?'not-allowed':'pointer', boxShadow:(loading||code.length<6)?'none':'0 4px 16px rgba(99,102,241,.35)' }}>
          {loading ? '⏳ Verifying...' : '→ Verify & Sign In'}
        </button>
        <div style={{ marginTop:12, fontSize:11, color:'#475569' }}>Code refreshes every 30 seconds in your authenticator app</div>
      </div>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const card  = { background:'rgba(30,41,59,.5)', border:'1px solid rgba(99,102,241,.2)', borderRadius:14, padding:24 };
const hd    = { margin:'0 0 10px', color:'#f1f5f9', fontSize:16, fontWeight:700 };
const sub   = { margin:'0 0 10px', color:'#94a3b8', fontSize:13, lineHeight:1.6 };
const errSt = { color:'#f87171', fontSize:12, background:'rgba(244,63,94,.08)', border:'1px solid rgba(244,63,94,.2)', borderRadius:6, padding:'8px 12px', marginBottom:8 };
const inp   = { width:'100%', padding:'10px 14px', background:'#0f172a', border:'1px solid rgba(255,255,255,.12)', borderRadius:8, color:'#f1f5f9', WebkitTextFillColor:'#f1f5f9', fontFamily:"'Inter',sans-serif", fontSize:14, outline:'none', boxSizing:'border-box', WebkitBoxShadow:'0 0 0 1000px #0f172a inset', caretColor:'#f1f5f9' };
const pb    = { width:'100%', padding:'11px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'#fff', fontFamily:"'Inter',sans-serif", fontWeight:700, fontSize:14, cursor:'pointer' };
